import {Box, Divider, LinearProgress, Typography} from '@mui/material'
import type {FC} from 'react'
import EquipmentTimeWindowCard from './EquipmentTimeWindowCard.tsx'
import {useSearchForEquipmentTimeWindowBasedOnSignalValueQuery} from '@apis/search'
import ErrorBoundary from '@shared/components/ErrorBoundary.tsx'
import HorizonatlScrollingContainer from '@shared/components/HorizontalScrollingContainer.tsx'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation.ts'

interface SearchEquipmentTimeWindowResultsProps {
  searchTerm: string
  productionAreaId?: string
  lineId?: string
  equipmentId?: string
}

// Mirrors the server's int()-based check (global_search._parse_int_value): an
// optional sign followed by digits, surrounding whitespace ignored.
const isIntegerSearchTerm = (term: string): boolean => /^[+-]?\d+$/.test(term.trim())

const SearchEquipmentTimeWindowResults: FC<SearchEquipmentTimeWindowResultsProps> = ({
  searchTerm,
  productionAreaId,
  lineId,
  equipmentId,
}) => {
  const [t] = useCustomTranslation('common')

  const isNumeric = isIntegerSearchTerm(searchTerm)

  // For a numeric term we fire two independent requests so results stream in
  // progressively: an exact (index-backed) match on the integer interval table and
  // a substring ILIKE on the string interval table. A non-numeric term can only
  // live in the string table, so it fires the string request alone.
  const {
    data: intResults,
    error: intError,
    isFetching: intIsFetching,
  } = useSearchForEquipmentTimeWindowBasedOnSignalValueQuery(
    {value: searchTerm, intervalType: 'int', productionAreaId, lineId, equipmentId},
    {skip: !searchTerm || !isNumeric},
  )
  const {
    data: stringResults,
    error: stringError,
    isFetching: stringIsFetching,
  } = useSearchForEquipmentTimeWindowBasedOnSignalValueQuery(
    {value: searchTerm, intervalType: 'string', productionAreaId, lineId, equipmentId},
    {skip: !searchTerm},
  )

  // Render whichever request resolves first, then extend with the other. The int
  // (exact) call is normally the quicker of the two, so its hits lead and the
  // string (ILIKE scan) hits extend the list as they arrive — we never block on
  // both. Int and string signals are distinct rows, so a plain concat can't
  // produce duplicate cards.
  const results = [...(intResults ?? []), ...(stringResults ?? [])]

  const isSearching = stringIsFetching || (isNumeric && intIsFetching)
  const hasError = Boolean(stringError) || (isNumeric && Boolean(intError))

  return (
    <Box sx={{p: 1}}>
      {results.length > 0 ? (
        <>
          <Divider>
            <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
              {results.length} {t(`appbar_search_time_windows.number_found`)}
            </Typography>
          </Divider>
          <HorizonatlScrollingContainer>
            {results.map((equipmentTimeWindowResult) => (
              <Box
                key={`appbar-search-time-windows-equipment-${equipmentTimeWindowResult.machine_sub_system.id}-${equipmentTimeWindowResult.signal.id}-${equipmentTimeWindowResult.t_start}`}
                sx={{
                  flex: '0 0 auto',
                  width: 500,
                  padding: 1,
                }}
              >
                <ErrorBoundary>
                  <EquipmentTimeWindowCard equipmentTimeWindow={equipmentTimeWindowResult} />
                </ErrorBoundary>
              </Box>
            ))}
          </HorizonatlScrollingContainer>
        </>
      ) : null}

      {isSearching ? (
        <Box sx={{mt: results.length > 0 ? 1 : 0}}>
          <Divider>
            <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
              {t(`appbar_search_time_windows.search_running`)}
            </Typography>
          </Divider>
          <LinearProgress sx={{width: '100%'}} />
        </Box>
      ) : null}

      {!isSearching && results.length === 0 && !hasError ? (
        <Divider>
          <Typography variant="h6" color="text.secondary">
            {t(`appbar_search_time_windows.no_results`)}
          </Typography>
        </Divider>
      ) : null}

      {!isSearching && results.length === 0 && hasError ? t('messages.unexpected_error') : null}
    </Box>
  )
}

export default SearchEquipmentTimeWindowResults
